import { useNavigate } from "@remix-run/react";
import React, { useCallback, useMemo, useState } from "react";
import { BellSimple } from "phosphor-react";

import { phTrackClientEvent } from "~/analytics.client";
import { Modal, useModal } from "~/components";
import { Events } from "~/config";
import { cn } from "~/lib/utils";
import { track } from "~/lib/journey-tracker.client";
import { useTrackOverlay } from "~/hooks/useTrackOverlay";

type NotificationItem = {
  id: string;
  type: string;
  system_profile_id?: string | null;
  title: string;
  body: string;
  payload: Record<string, unknown>;
  read_at?: string | null;
  created_at: string;
};

type NotificationListResponse = {
  items: NotificationItem[];
  unread_count: number;
  limit: number;
  offset: number;
};

interface NotificationBellProps {
  unreadCount: number;
  onUnreadCountChange?: (count: number) => void;
  className?: string;
  iconSize?: number;
}

function formatRelativeTime(isoTimestamp: string): string {
  const timestamp = new Date(isoTimestamp).getTime();
  if (Number.isNaN(timestamp)) {
    return "Just now";
  }

  const diffMs = Date.now() - timestamp;
  const diffSeconds = Math.max(Math.floor(diffMs / 1000), 0);

  if (diffSeconds < 60) return "Just now";
  if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)}m ago`;
  if (diffSeconds < 86400) return `${Math.floor(diffSeconds / 3600)}h ago`;
  return `${Math.floor(diffSeconds / 86400)}d ago`;
}

/** Mounts with the notifications modal, so it records the list opening and closing. */
function TrackNotificationsOverlay({ unread }: { unread: number }) {
  useTrackOverlay("notifications", { unread });
  return null;
}

export default function NotificationBell({
  unreadCount,
  onUnreadCountChange,
  className,
  iconSize = 22,
}: NotificationBellProps) {
  const notificationModal = useModal();
  const navigate = useNavigate();

  const [items, setItems] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const displayUnreadCount = useMemo(
    () => (unreadCount > 99 ? "99+" : `${unreadCount}`),
    [unreadCount]
  );

  const loadNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch("/api/notifications?limit=50");
      if (!response.ok) {
        throw new Error(`Failed to load notifications (${response.status})`);
      }

      const data = (await response.json()) as NotificationListResponse;
      setItems(data.items || []);
      if (typeof data.unread_count === "number") {
        onUnreadCountChange?.(data.unread_count);
      }
    } catch (error) {
      console.error("[NotificationBell] Failed to load notifications:", error);
    } finally {
      setLoading(false);
    }
  }, [onUnreadCountChange]);

  const postAction = useCallback(
    async (body: Record<string, unknown>) => {
      const response = await fetch("/api/notifications", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error(`Notification action failed (${response.status})`);
      }

      return response.json();
    },
    []
  );

  const handleOpen = useCallback(() => {
    notificationModal.open();
    void loadNotifications();
  }, [loadNotifications, notificationModal]);

  const markOneAsRead = useCallback(
    async (notificationId: string) => {
      const notification = items.find((item) => item.id === notificationId);
      if (!notification || notification.read_at) {
        return;
      }

      try {
        const result = (await postAction({
          action: "mark_read",
          notificationId,
          markAll: false,
        })) as { unread_count?: number };

        setItems((prev) =>
          prev.map((item) =>
            item.id === notificationId
              ? { ...item, read_at: new Date().toISOString() }
              : item
          )
        );

        if (typeof result.unread_count === "number") {
          onUnreadCountChange?.(result.unread_count);
        } else {
          onUnreadCountChange?.(Math.max(unreadCount - 1, 0));
        }
      } catch (error) {
        console.error("[NotificationBell] Failed to mark notification read:", error);
      }
    },
    [items, onUnreadCountChange, postAction, unreadCount]
  );

  const handleMarkAllAsRead = useCallback(async () => {
    setActionLoading(true);
    try {
      const result = (await postAction({
        action: "mark_read",
        markAll: true,
      })) as { unread_count?: number };

      setItems((prev) =>
        prev.map((item) =>
          item.read_at ? item : { ...item, read_at: new Date().toISOString() }
        )
      );

      if (typeof result.unread_count === "number") {
        onUnreadCountChange?.(result.unread_count);
      } else {
        onUnreadCountChange?.(0);
      }
    } catch (error) {
      console.error("[NotificationBell] Failed to mark all notifications read:", error);
    } finally {
      setActionLoading(false);
    }
  }, [onUnreadCountChange, postAction]);

  const handleNotificationClick = useCallback(
    async (item: NotificationItem) => {
      if (!item.read_at) {
        await markOneAsRead(item.id);
      }

      const profileIdFromPayload =
        typeof item.payload?.profile_id === "string" &&
        item.payload.profile_id.trim().length > 0
          ? item.payload.profile_id
          : null;
      const targetProfileId = item.system_profile_id || profileIdFromPayload;

      if (targetProfileId) {
        phTrackClientEvent(Events.CLICKED_PENDING_NOTIFICATION, {
          profile_id: targetProfileId,
          notification_id: item.id,
          notification_type: item.type,
        });
        track("notification_tapped", {
          type: item.type,
          persona_id: targetProfileId,
        });

        notificationModal.close();
        navigate(`/user-profile/${targetProfileId}`);
      }
    },
    [markOneAsRead, navigate, notificationModal]
  );

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        className={cn(
          "relative p-2 rounded-md hover:bg-componentBackground transition-colors text-text",
          className
        )}
        aria-label="Notifications"
      >
        <BellSimple size={iconSize} weight="duotone" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[1.15rem] h-[1.15rem] px-1 rounded-full text-[0.65rem] font-bold bg-gradient-to-t from-red-600 to-rose-700 text-white border border-white/20">
            {displayUnreadCount}
          </span>
        )}
      </button>

      <Modal {...notificationModal.modalProps}>
        <TrackNotificationsOverlay unread={unreadCount} />
        <div className="w-[min(92vw,24rem)] p-4 text-text">
          <div className="border-b border-white/10 pb-3 mb-3 pr-12">
            <p className="text-base font-semibold">Notifications</p>
            <div className="mt-1 flex items-center gap-2">
              <p className="text-xs text-text1">
                {unreadCount > 0
                  ? `${unreadCount} unread`
                  : "You are all caught up"}
              </p>
              {unreadCount > 0 && (
                <>
                  <span className="text-xs text-text1/60">•</span>
                  <button
                    type="button"
                    onClick={handleMarkAllAsRead}
                    disabled={actionLoading}
                    className="text-xs font-semibold text-primary hover:opacity-80 disabled:opacity-50"
                  >
                    Mark all read
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="max-h-[60vh] overflow-y-auto space-y-2 pr-1">
            {loading ? (
              <div className="text-sm text-text1 py-6 text-center">
                Loading notifications...
              </div>
            ) : items.length === 0 ? (
              <div className="text-sm text-text1 py-6 text-center">
                No notifications yet.
              </div>
            ) : (
              items.map((item) => {
                const avatarUrl =
                  typeof item.payload?.avatar_url === "string"
                    ? item.payload.avatar_url
                    : "/default-profile.jpg";
                const isUnread = !item.read_at;

                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => void handleNotificationClick(item)}
                    className={cn(
                      "w-full text-left rounded-lg p-3 border transition-colors",
                      isUnread
                        ? "border-primary/40 bg-primary/10"
                        : "border-white/10 bg-componentBackground/40 hover:bg-componentBackground/70"
                    )}
                  >
                    <div className="flex items-start gap-3">
                      <img
                        src={avatarUrl}
                        alt="Notification avatar"
                        className="w-10 h-10 rounded-full object-cover border border-white/10"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold truncate">{item.title}</p>
                          {isUnread && (
                            <span className="w-2 h-2 rounded-full bg-primary shrink-0" />
                          )}
                        </div>
                        <p className="text-xs text-text1 mt-1">{item.body}</p>
                        <p className="text-[0.65rem] text-text1 mt-2">
                          {formatRelativeTime(item.created_at)}
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      </Modal>
    </>
  );
}
